/**
 * Constrained RPG "almost-isometric" camera.
 *
 * - Always framed on the player (orbit target = player). No free pan.
 * - Yaw rotates around the player (drag).
 * - Fixed pitch for the iso feel.
 * - Zoom clamps orbit distance within a slight range — per the brief,
 *   zooming in is the only way to get a closer/panned-in view.
 *
 * This is intentionally NOT three's OrbitControls (which pans by default).
 * Frontend agent: tune pitch/distance/feel against the running demo.
 */
import * as THREE from 'three';
import type { CameraConfig, Vec2 } from '../types';

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** The vertical FOV every screen starts from, degrees. */
export const BASE_FOV_DEG = 50;

/** Height of the orbit target above the player's feet — `update`'s default `headHeight`. */
const TARGET_HEIGHT_M = 1.3;

/**
 * Slack on the side constraint. The frame's footprint on the ground is a
 * trapezoid, wider at the top, so its slanted side passes slightly closer to
 * the player than the straight-across distance at the player's depth. 5%
 * covers that at every aspect from 9:19.5 to 21:9 (`camera-controller.test.ts`
 * ray-casts the real frame to prove it).
 */
const SIDE_SLACK = 1.05;

/**
 * The orbit distance, metres, that shows at least `radius` metres of ground
 * around the player in EVERY direction — behind, ahead and to both sides — at
 * the fixed `BASE_FOV_DEG` lens. Never less than `minDistance`.
 *
 * Janeks, 2026-10-16, after a bot shot him from off-screen: *"set the viewport
 * max zoom, so that on all screens everybody has the same fov"*. A fixed orbit
 * does not give that: the frame is as tall on every screen but its WIDTH
 * follows the aspect, so at the arena's 30 m orbit a 16:9 monitor sees 26 m to
 * each side and a portrait phone 7. And because the camera looks down at
 * 55 deg, the bottom edge is always the near one — 12.6 m behind the player
 * against 27.6 m ahead.
 *
 * Pulled BACK rather than widened: the first version of this widened the FOV
 * instead, which gives the same width at the player but an 83 deg fish-eye on
 * a 9:19.5 phone whose top edge landed 89 m ahead (the ring is 22 m). The
 * longer orbit keeps the lens and roughly halves that. It cannot remove it: a
 * screen that tall and 24 m wide is ~52 m long on the ground even looking
 * straight down, so a portrait phone always sees further ahead than a monitor.
 * Only a sight mask could equalise that, and nothing draws one yet.
 *
 * Two constraints, whichever binds:
 *  - sides: the half-width at the player's depth must be `radius` (plus slack);
 *  - behind: the bottom ray must land `radius` behind the player.
 *
 * Pure, so the arena's promise — "a bot that can see you is on your screen" —
 * is testable without a renderer. `BOT_SIGHT_RANGE_M` on the server is this
 * radius.
 */
export function orbitForGroundRadius(aspect: number, minDistance: number, pitch: number, radius: number): number {
  const half = (BASE_FOV_DEG * Math.PI) / 360;
  // Sides: the player's feet sit `d + 1.3 sin(pitch)` down the view axis, and
  // lateral ground points share that depth.
  const sides = (radius * SIDE_SLACK) / (Math.tan(half) * aspect) - TARGET_HEIGHT_M * Math.sin(pitch);
  // Behind: the bottom ray, depressed by pitch + fov/2, meets the ground at
  // `(1.3 + d sin p) / tan(p + fov/2)` from the camera, which stands
  // `d cos p` back from the player. Linear in d, so solve it directly.
  const low = Math.tan(pitch + half);
  const behind = (radius + TARGET_HEIGHT_M / low) / (Math.cos(pitch) - Math.sin(pitch) / low);
  return Math.max(minDistance, sides, behind);
}

export class CameraController {
  readonly camera: THREE.PerspectiveCamera;
  private yaw = 0;
  private distance: number;
  private readonly cfg: CameraConfig;
  private readonly target = new THREE.Vector3();

  constructor(aspect: number, cfg: CameraConfig) {
    this.cfg = cfg;
    this.distance = cfg.defaultDistance;
    this.camera = new THREE.PerspectiveCamera(BASE_FOV_DEG, aspect, 0.1, 500);
  }

  /** Current yaw (radians) — passed to the sim so input is camera-relative. */
  get yawValue(): number {
    return this.yaw;
  }

  /**
   * Current orbit distance in metres — the zoom level, read by the debug
   * overlay.
   *
   * Exposed alongside `minDistance`/`maxDistance` rather than on its own: the
   * number is only meaningful against the clamp it lives inside, and the point
   * of showing it is to retune that clamp by feel.
   */
  get distanceValue(): number {
    return this.orbitDistance();
  }

  get minDistance(): number {
    return this.cfg.minDistance;
  }

  get maxDistance(): number {
    return this.cfg.maxDistance;
  }

  /** Rotate the orbit by a horizontal drag delta (pixels). */
  rotate(deltaPixels: number): void {
    this.yaw -= deltaPixels * this.cfg.rotateSpeed;
  }

  /** Zoom in/out by wheel notches (positive = zoom out), clamped. */
  zoom(deltaNotches: number): void {
    this.distance = clamp(
      this.distance + deltaNotches * this.cfg.zoomSpeed,
      this.cfg.minDistance,
      this.cfg.maxDistance,
    );
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * The orbit actually flown: the zoom level, pulled back on a narrow screen
   * far enough to keep `minGroundRadius` in frame, if the map asks for it.
   */
  private orbitDistance(): number {
    const r = this.cfg.minGroundRadius;
    return r === undefined
      ? this.distance
      : orbitForGroundRadius(this.camera.aspect, this.distance, this.cfg.pitch, r);
  }

  /** Reposition the camera to orbit the player. Call once per frame. */
  update(playerPos: Vec2, headHeight = TARGET_HEIGHT_M): void {
    this.target.set(playerPos.x, headHeight, playerPos.z);
    const distance = this.orbitDistance();
    const horiz = distance * Math.cos(this.cfg.pitch);
    const height = distance * Math.sin(this.cfg.pitch);
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * horiz,
      this.target.y + height,
      this.target.z + Math.cos(this.yaw) * horiz,
    );
    this.camera.lookAt(this.target);
  }
}
